/**
 * Sentry beforeSend Hook
 *
 * This function is called before an event is sent to Sentry.
 * It allows for custom filtering and modification of events.
 *
 * @param event - The error event to be sent
 * @param hint - Additional context about the event
 * @returns The event to send, or null to drop the event
 */
// Using any types to avoid direct dependency on @sentry/types
// The actual Sentry SDK will provide proper types at runtime
export function beforeSend(event: any, hint?: any): any | null {
  // ============================================
  // Filter crawler/bot requests
  // ============================================
  const userAgent = event.request?.headers?.['user-agent'] || '';

  // Bot/crawler patterns to filter
  const botPatterns = [
    /bot/i,
    /crawler/i,
    /spider/i,
    /broken-link-checker/i,
    /googlebot/i,
    /bingbot/i,
    /slurp/i,
    /duckduckbot/i,
    /baiduspider/i,
    /yandexbot/i,
    /facebookexternalhit/i,
    /twitterbot/i,
    /rogerbot/i,
    /linkedinbot/i,
    /embedly/i,
    /quora link preview/i,
    /showyoubot/i,
    /outbrain/i,
    /pinterest/i,
    /slackbot/i,
    /vkShare/i,
    /W3C_Validator/i,
    /redditbot/i,
    /applebot/i,
    /whatsapp/i,
    /flipboard/i,
    /tumblr/i,
    /bitlybot/i,
    /skypeuripreview/i,
    /nuzzel/i,
    /discordbot/i,
    /qwantify/i,
    /pinterestbot/i,
    /bitbot/i,
    /ahrefsbot/i,
    /semrushbot/i,
    /dotbot/i,
    /mj12bot/i,
  ];

  const isBotRequest = botPatterns.some((pattern) => pattern.test(userAgent));

  if (isBotRequest) {
    return null;
  }

  // ============================================
  // Filter network errors from 3rd-party domains
  // ============================================
  const errorMessage = event.message || event.exception?.values?.[0]?.value || '';
  const requestUrl = event.request?.url || '';

  // Network error patterns
  const networkErrorPatterns = [
    /fetch failed/i,
    /network request failed/i,
    /failed to fetch/i,
    /load failed/i,
    /cancelled/i,
  ];

  const isNetworkError = networkErrorPatterns.some((pattern) => pattern.test(errorMessage));

  if (isNetworkError) {
    // Allow network errors from our own domains (skin-seoul.com)
    const ourDomains = ['skin-seoul.com'];
    const isOurDomain = ourDomains.some((domain) => requestUrl.includes(domain));

    // Ignore network errors from 3rd-party domains
    if (!isOurDomain) {
      return null;
    }
  }

  // ============================================
  // Filter by breadcrumbs/tags
  // ============================================
  const breadcrumbs = event.breadcrumbs || [];

  // Check if error originated from external scripts
  const externalScriptDomains = ['cookielaw.org', 'facebook.com', 'google-analytics.com'];

  const hasExternalScriptError = breadcrumbs.some((breadcrumb: any) => {
    const category = breadcrumb.category || '';
    const message = breadcrumb.message || '';

    return category.includes('xhr') && externalScriptDomains.some((domain) => message.includes(domain));
  });

  if (hasExternalScriptError) {
    return null;
  }

  // ============================================
  // Allow the event to be sent
  // ============================================
  return event;
}
